// @flow
import * as React from "react";

type Props<V> = {
  value: V,
  onInvalidate: () => Promise<V>,
  children: ({
    value: V,
    update: (updater: (V) => $Shape<V>, promise: Promise<mixed>) => void
  }) => React.Node
};

type State<V> = {
  value: V,
  pendingUpdates: Array<*>
};

function applyUpdate(value, update) {
  if (value == null) {
    return value;
  } else {
    return {
      ...value,
      ...update.updater(value)
    };
  }
}

export default class Optimistic extends React.Component<Props<*>, State<*>> {
  state = {
    value: this.props.value,
    pendingUpdates: []
  };

  stage(updater) {
    let update = { updater };

    this.setState(state => ({
      pendingUpdates: [...state.pendingUpdates, update]
    }));

    return update;
  }

  checkout() {
    return this.state.pendingUpdates.reduce(applyUpdate, this.state.value);
  }

  commit(update) {
    this.setState(state => ({
      value: applyUpdate(state.value, update),
      pendingUpdates: state.pendingUpdates.filter(u => u !== update)
    }));
  }

  drop(update) {
    this.setState(state => ({
      pendingUpdates: state.pendingUpdates.filter(u => u !== update)
    }));
  }

  rebase(update, value) {
    this.setState(state => {
      let index = state.pendingUpdates.indexOf(update);

      return {
        value: value,
        pendingUpdates: state.pendingUpdates.filter((u, i) => i > index)
      };
    });
  }

  update = (updater, promise) => {
    let update = this.stage(updater);

    promise.then(
      () => {
        this.commit(update);
      },
      () => {
        this.drop(update);
      }
    );
  };

  invalidate = options => {
    let update = this.stage(() => null);

    return this.props.onInvalidate(options).then(
      value => {
        this.rebase(update, value);
      },
      error => {
        this.drop(update);
        throw error;
      }
    );
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    let shouldFlushState = false;

    if (nextProps.value == null || this.props.value == null) {
      shouldFlushState = nextProps.value !== this.props.value;
    } else {
      shouldFlushState = nextProps.value.id !== this.props.value.id;
    }

    if (shouldFlushState) {
      this.setState({
        value: nextProps.value,
        pendingUpdates: []
      });
    }
  }

  render() {
    return this.props.children({
      value: this.checkout(),
      update: this.update,
      invalidate: this.invalidate
    });
  }
}



// WEBPACK FOOTER //
// ./src/components/Tournaments/Optimistic.js